import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { ArrowLeft } from 'lucide-react';
import { getProduct } from '../services/api';
import { getShippingCost } from '../utils/shipping';

const Checkout = () => {
  const { items, getCartTotal } = useCart();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    contact: '',
    emailNews: false,
    country: 'Pakistan',
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    postalCode: '',
    phone: '',
    saveInfo: false
  });

  const [discountCode, setDiscountCode] = useState('');
  const [, setDiscountApplied] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('Cash on Delivery');

  const formatPrice = (price) => {
    if (typeof price === 'string') {
      return price;
    }
    return `Rs ${price.toLocaleString()}`;
  };

  const formatPrescriptionValue = (value) => {
    return value || 'null';
  };

  const renderPrescriptionDetails = (prescription, productType) => {
    if (!prescription) return null;

    const { rightEye, leftEye } = prescription;
    
    let hasRightPrescription, hasLeftPrescription;
    
    if (productType === 'lenses') {
      hasRightPrescription = rightEye?.distance?.sph;
      hasLeftPrescription = leftEye?.distance?.sph;
    } else {
      hasRightPrescription = rightEye?.distance?.sph || rightEye?.distance?.cyl || rightEye?.distance?.axis || 
                            rightEye?.near?.sph || rightEye?.near?.cyl || rightEye?.near?.axis;
      hasLeftPrescription = leftEye?.distance?.sph || leftEye?.distance?.cyl || leftEye?.distance?.axis || 
                           leftEye?.near?.sph || leftEye?.near?.cyl || leftEye?.near?.axis;
    }
    
    if (!hasRightPrescription && !hasLeftPrescription) return null;
    
    return (
      <div className="mt-1 text-xs text-gray-600">
        <div className="mb-1">
          <span className="font-medium">Prescription - Right:</span>
          {hasRightPrescription ? (
            <div className="ml-2">
              {(rightEye?.distance?.sph || (productType === 'eyeglasses' && (rightEye?.distance?.cyl || rightEye?.distance?.axis))) && (
                <div>
                  <div>Distance:</div>
                  <div className="ml-2">
                    {productType === 'lenses' ? (
                      `(SPH: ${formatPrescriptionValue(rightEye.distance.sph)})`
                    ) : (
                      `(SPH: ${formatPrescriptionValue(rightEye.distance.sph)}, CYL: ${formatPrescriptionValue(rightEye.distance.cyl)}, AXIS: ${formatPrescriptionValue(rightEye.distance.axis)})`
                    )}
                  </div>
                </div>
              )}
              {productType === 'eyeglasses' && (rightEye?.near?.sph || rightEye?.near?.cyl || rightEye?.near?.axis) && (
                <div className="mt-1">
                  <div>Near:</div>
                  <div className="ml-2">(SPH: {formatPrescriptionValue(rightEye.near.sph)}, CYL: {formatPrescriptionValue(rightEye.near.cyl)}, AXIS: {formatPrescriptionValue(rightEye.near.axis)})</div>
                </div>
              )}
            </div>
          ) : (
            <div className="ml-2">
              <div>Distance: (SPH: )</div>
            </div>
          )}
        </div>

        <div>
          <span className="font-medium">Prescription - Left:</span>
          {hasLeftPrescription ? (
            <div className="ml-2">
              {(leftEye?.distance?.sph || (productType === 'eyeglasses' && (leftEye?.distance?.cyl || leftEye?.distance?.axis))) && (
                <div>
                  <div>Distance:</div>
                  <div className="ml-2">
                    {productType === 'lenses' ? (
                      `(SPH: ${formatPrescriptionValue(leftEye.distance.sph)})`
                    ) : (
                      `(SPH: ${formatPrescriptionValue(leftEye.distance.sph)}, CYL: ${formatPrescriptionValue(leftEye.distance.cyl)}, AXIS: ${formatPrescriptionValue(leftEye.distance.axis)})`
                    )}
                  </div>
                </div>
              )}
              {productType === 'eyeglasses' && (leftEye?.near?.sph || leftEye?.near?.cyl || leftEye?.near?.axis) && (
                <div className="mt-1">
                  <div>Near:</div>
                  <div className="ml-2">(SPH: {formatPrescriptionValue(leftEye.near.sph)}, CYL: {formatPrescriptionValue(leftEye.near.cyl)}, AXIS: {formatPrescriptionValue(leftEye.near.axis)})</div>
                </div>
              )}
            </div>
          ) : (
            <div className="ml-2">
              <div>Distance: (SPH: )</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDiscountApply = () => {
    if (discountCode.trim()) {
      setDiscountApplied(true);
      // Add discount logic here
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate stock availability before checkout
    try {
      const stockChecks = await Promise.all(
        items.map(async (item) => {
          try {
            const productData = await getProduct(item.id);
            // getProduct returns { data: [{...}] } - array structure
            const productItem = productData.data && productData.data.length > 0 ? productData.data[0] : null;
            if (!productItem) {
              return { item, currentStock: 0, isValid: false };
            }
            
            const currentStock = productItem.quantity !== undefined 
              ? productItem.quantity 
              : (productItem.inStock ? 999 : 0);
            
            return {
              item,
              currentStock,
              isValid: currentStock >= item.quantity
            };
          } catch (error) {
            console.error(`Error checking stock for product ${item.id}:`, error);
            return { item, currentStock: 0, isValid: false };
          }
        })
      );

      // Check if any item is out of stock
      const outOfStockItems = stockChecks.filter(check => !check.isValid);
      if (outOfStockItems.length > 0) {
        const itemNames = outOfStockItems.map(check => check.item.name).join(', ');
        alert(`The following items are no longer available in the requested quantity:\n${itemNames}\n\nPlease update your cart and try again.`);
        return;
      }
    } catch (error) {
      console.error('Error validating stock:', error);
      // Continue with checkout if stock check fails (graceful degradation)
    }
    
    // Prepare order data
    const subtotal = getCartTotal();
    const shippingCost = getShippingCost(subtotal);

    const orderData = {
      customer: {
        contact: formData.contact,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        address: formData.address,
        apartment: formData.apartment,
        city: formData.city,
        postalCode: formData.postalCode,
        country: formData.country
      },
      items: items.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image: item.image,
        prescription: item.prescription,
        productType: item.productType
      })),
      subtotal,
      shipping: shippingCost,
      total: subtotal + shippingCost,
      paymentMethod: paymentMethod,
      discountCode: discountCode || null
    };

    // Navigate to success page with order data
    navigate('/checkout/success', { state: { orderData } });
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <Helmet>
          <title>Checkout - Mughal Ainak Mahal</title>
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-secondary mb-4">Your cart is empty</h1>
          <Link to="/cart" className="text-primary hover:underline">
            Return to cart
          </Link>
        </div>
      </div>
    );
  }

  const subtotal = getCartTotal();
  const shippingCost = getShippingCost(subtotal);
  const total = subtotal + shippingCost;
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Checkout - Mughal Ainak Mahal</title>
        <meta name="description" content="Complete your order at Mughal Ainak Mahal" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/cart"
            className="flex items-center text-gray-600 hover:text-primary transition-colors duration-200 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Return to cart
          </Link>
          <h1 className="text-3xl font-bold text-secondary">Checkout</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Contact Section */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-secondary">Contact</h2>
                  <Link to="#" className="text-primary hover:underline text-sm">
                    Sign in
                  </Link>
                </div>
                <input
                  type="text"
                  name="contact"
                  value={formData.contact}
                  onChange={handleInputChange}
                  placeholder="Email or mobile phone number"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
                <label className="flex items-center mt-3">
                  <input
                    type="checkbox"
                    name="emailNews"
                    checked={formData.emailNews}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-600">Email me with news and offers</span>
                </label>
              </div>

              {/* Delivery Section */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-secondary mb-4">Delivery</h2>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country/Region
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="Pakistan">Pakistan</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Apartment, suite, etc. (optional)
                  </label>
                  <input
                    type="text"
                    name="apartment"
                    value={formData.apartment}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Postal code (optional)
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                    <span className="ml-1 text-gray-400 cursor-help">?</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="saveInfo"
                    checked={formData.saveInfo}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-600">Save this information for next time</span>
                </label>
              </div>

              {/* Payment Method Section */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-secondary mb-4">Payment method</h2>
                <div
                  onClick={() => setPaymentMethod('Cash on Delivery')}
                  className={`border-2 rounded-md p-4 cursor-pointer ${
                    paymentMethod === 'Cash on Delivery' ? 'border-primary' : 'border-gray-300'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Cash on Delivery</span>
                  </div>
                </div>
              </div>

              {/* Checkout Button */}
              <div className="flex justify-between">
                <Link
                  to="/cart"
                  className="text-gray-600 hover:text-primary transition-colors duration-200"
                >
                  ← Return to cart
                </Link>
                <button
                  type="submit"
                  className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors duration-200"
                >
                  Checkout
                </button>
              </div>
            </form>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
              {/* Cart Items - Scrollable */}
              <div className="max-h-96 overflow-y-auto mb-4">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.id} className="flex items-start space-x-3 pb-4 border-b border-gray-200 last:border-0">
                      <div className="flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-1">
                          <h3 className="text-sm font-semibold text-secondary">{item.name}</h3>
                          <span className="text-sm font-semibold text-secondary ml-2">×{item.quantity}</span>
                        </div>
                        {renderPrescriptionDetails(item.prescription, item.productType)}
                        <p className="text-sm font-semibold text-secondary mt-1">
                          {formatPrice(item.price)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Discount Code - Inside order summary section */}
              <div className="mb-4 border-t border-gray-200 pt-4">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    placeholder="Discount code"
                    className="flex-1 max-w-[75%] px-3 py-2 text-sm border-2 border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={handleDiscountApply}
                    className="px-3 py-2 text-sm bg-white border-2 border-primary text-primary rounded-md font-semibold hover:bg-primary hover:text-white transition-colors duration-200 whitespace-nowrap"
                  >
                    Apply
                  </button>
                </div>
              </div>

              {/* Order Totals */}
              <div className="space-y-3 border-t border-gray-200 pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal {totalItems} {totalItems === 1 ? 'item' : 'items'}</span>
                  <span className="font-semibold">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-semibold">{shippingCost === 0 ? 'Free' : formatPrice(shippingCost)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-3">
                  <span>Total</span>
                  <span className="text-primary">PKR {formatPrice(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
